import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  TARGET_ROLE_CONFIG,
  TARGET_ROLE_OPTIONS,
  createStaffInvitation,
  listStaffInvitations,
  resendStaffInvitation,
  revokeStaffInvitation,
} from '../../api/staffInvitations.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'

/**
 * Admin — Staff invitations.
 *
 * Polymorphic invitation surface for the five non-doctor staff roles
 * (pharmacy_staff / lab_staff / radiology_staff / ambulance_staff /
 * org_admin). Mirrors `InvitationsPage.jsx` (which handles doctors)
 * but with a dynamic target picker driven by `TARGET_ROLE_CONFIG`.
 *
 * Doctor invitations stay on their own page because they carry
 * doctor-only fields (license number, specialty, place of work). This
 * page deliberately keeps the form lean — staff invites only need
 * email + name + target.
 */
export default function StaffInvitationsPage() {
  const qc = useQueryClient()

  const invitesQ = useQuery({
    queryKey: ['staff-invitations'],
    queryFn: () => listStaffInvitations(),
  })

  // ---- Form state ---------------------------------------------------
  // Target selection is two-step: first pick the role, then pick the
  // matching target (pharmacy / lab / radiology / ambulance / org).
  // Switching roles resets the target to avoid sending a "wrong shape"
  // payload that the backend's CheckConstraint would 400 on.
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    target_role: 'pharmacy_staff',
    target_id: '',          // generic "the chosen FK" — mapped onto the
                            // role-specific FK field at submit time.
    is_admin: false,
    role_title: '',         // ambulance-only descriptive label
                            // (Paramedic / EMT / Driver / Dispatcher /
                            // On-call Clinician / Nurse). Other roles
                            // ignore this field server-side.
    note: '',
  })
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [k]: v }))
  }

  // Reset target_id (and the ambulance-only role_title) whenever
  // target_role changes so stale values from a previous role can't
  // accidentally submit. Also wipes any old create-mutation error so
  // the form looks clean on each role switch.
  useEffect(() => {
    setForm((f) => ({ ...f, target_id: '', role_title: '' }))
    create.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.target_role])

  const cfg = TARGET_ROLE_CONFIG[form.target_role]

  // Per-role target dropdown is a separate React Query so swapping roles
  // doesn't refetch the full list — each role's list cache survives
  // navigation back to it.
  const targetsQ = useQuery({
    queryKey: ['invite-targets', form.target_role],
    queryFn: cfg.fetcher,
    enabled: Boolean(cfg),
    staleTime: 60_000,
  })

  const create = useMutation({
    mutationFn: () => {
      // Translate the role-agnostic `target_id` into the role-specific
      // FK field the backend expects (e.g. target_pharmacy=42).
      const payload = {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        target_role: form.target_role,
        is_admin: form.is_admin,
        note: form.note.trim(),
        [cfg.fkField]: form.target_id ? Number(form.target_id) : null,
      }
      // role_title is only meaningful for ambulance_staff invites today
      // (AmbulanceStaffLink has the field; the other StaffLink models
      // don't). Send blank for other roles so the backend default (`""`)
      // applies cleanly and the field stays out of unrelated rows.
      if (form.target_role === 'ambulance_staff') {
        payload.role_title = (form.role_title || '').trim()
      }
      return createStaffInvitation(payload)
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['staff-invitations'] })
      // Truthful reporting per task #35: surface the actual email dispatch
      // outcome to the admin instead of silently claiming success. If the
      // provider rejected the send, we still keep the invitation (it was
      // written to the DB) and offer a copy-link fallback so the admin has
      // a manual recovery path.
      setLastDispatch({
        email: data?.email,
        status: data?.email_status || null,
        invite_url: data?.invite_url,
      })
      // Keep target_role + is_admin (admin often invites several of the
      // same role in a row); reset the per-person fields.
      setForm((f) => ({
        ...f,
        email: '',
        first_name: '',
        last_name: '',
        target_id: '',
        role_title: '',
        note: '',
      }))
    },
  })

  const revoke = useMutation({
    mutationFn: (id) => revokeStaffInvitation(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-invitations'] }),
  })

  const resend = useMutation({
    mutationFn: (id) => resendStaffInvitation(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['staff-invitations'] })
      // Same truthful-reporting contract as create above — surface whether
      // the resend actually reached the provider.
      setLastDispatch({
        email: data?.email,
        status: data?.email_status || null,
        invite_url: data?.invite_url,
      })
    },
  })

  // Banner state — tracks the outcome of the most recent create/resend so
  // we can render a persistent status message (task #35). Null when nothing
  // has been dispatched in this session yet, or after the admin dismisses.
  const [lastDispatch, setLastDispatch] = useState(null)

  async function copyLink(url) {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // Silent — the URL is visible on the row so manual copy still works.
    }
  }

  // ---- Filter chips for the list ------------------------------------
  // Quick filter so the admin can isolate "pending lab invites" without
  // typing into a search box. Doesn't hit the API — purely client-side
  // filter over the already-loaded list.
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const filteredInvites = useMemo(() => {
    const rows = invitesQ.data || []
    return rows.filter((inv) => {
      if (statusFilter !== 'all' && inv.effective_status !== statusFilter) return false
      if (roleFilter !== 'all' && inv.target_role !== roleFilter) return false
      return true
    })
  }, [invitesQ.data, statusFilter, roleFilter])

  // Disable submit until the four mandatory fields are present. We don't
  // require last_name / first_name (some staff identify by single name);
  // email + role + target are the floor.
  const canSubmit = (
    Boolean(form.email.trim())
    && Boolean(form.target_role)
    && Boolean(form.target_id)
    && !create.isPending
  )

  return (
    <div className="space-y-4">
      {/* ─── Invite form ──────────────────────────────────────────── */}
      <Card>
        <CardHeader
          title="Invite staff"
          subtitle={
            'Pharmacies, labs, radiology centres, ambulance services, and ' +
            'organisation admins. The recipient gets a one-link email; ' +
            'they set their own password on accept.'
          }
        />

        {/* ─── Dispatch outcome banner (task #35) ─────────────────────
            Surfaces the actual email delivery result of the most recent
            create or resend. Success is quiet-but-visible; failure gives
            a copy-link recovery path so the admin can still onboard the
            recipient even when Postmark rejected the send. */}
        {lastDispatch && (
          <div
            className={
              'mb-3 rounded border p-3 text-sm ' +
              (lastDispatch.status === 'sent'
                ? 'bg-green-50 border-green-200 text-green-900'
                : lastDispatch.status === 'skipped'
                  ? 'bg-slate-50 border-slate-200 text-slate-800'
                  : 'bg-amber-50 border-amber-300 text-amber-900')
            }
          >
            {lastDispatch.status === 'sent' && (
              <p>
                Invitation email sent to <strong>{lastDispatch.email}</strong>.
              </p>
            )}
            {lastDispatch.status === 'skipped' && (
              <p>
                Invitation recorded for <strong>{lastDispatch.email || 'this recipient'}</strong>.
                No email was dispatched because the invitation has no address on file.
              </p>
            )}
            {lastDispatch.status !== 'sent' && lastDispatch.status !== 'skipped' && (
              <div className="space-y-2">
                <p className="font-medium">
                  Invitation was created, but email delivery to{' '}
                  <strong>{lastDispatch.email}</strong> failed.
                </p>
                <p>Send them this link directly, or click <em>Resend email</em> on the row below to try again:</p>
                {lastDispatch.invite_url && (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-white px-2 py-1 text-xs">
                      {lastDispatch.invite_url}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyLink(lastDispatch.invite_url)}
                      className="rounded border border-amber-400 bg-white px-2 py-1 text-xs hover:bg-amber-100"
                    >
                      Copy link
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="mt-2 text-right">
              <button
                type="button"
                onClick={() => setLastDispatch(null)}
                className="text-xs underline opacity-70 hover:opacity-100"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledSelect
            label="Role *"
            value={form.target_role}
            onChange={set('target_role')}
          >
            {TARGET_ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </LabeledSelect>

          <LabeledSelect
            label={`${cfg.targetLabel} *`}
            value={form.target_id}
            onChange={set('target_id')}
            disabled={targetsQ.isLoading || !targetsQ.data?.length}
          >
            <option value="">
              {targetsQ.isLoading
                ? 'Loading…'
                : (targetsQ.data?.length ? `Select a ${cfg.targetLabel.toLowerCase()}…` : `No ${cfg.targetLabel.toLowerCase()}s on file`)}
            </option>
            {(targetsQ.data || []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name || `#${row.id}`}
              </option>
            ))}
          </LabeledSelect>

          <LabeledInput
            label="Email *"
            type="email"
            value={form.email}
            onChange={set('email')}
            placeholder="recipient@example.com"
          />
          <div /> {/* spacer for grid alignment */}

          <LabeledInput
            label="First name"
            value={form.first_name}
            onChange={set('first_name')}
            placeholder="Optional — helps personalise the email"
          />
          <LabeledInput
            label="Last name"
            value={form.last_name}
            onChange={set('last_name')}
          />
        </div>

        <label className="flex items-center gap-2 mt-3 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={form.is_admin}
            onChange={set('is_admin')}
          />
          <span>
            Grant <b>admin</b> rights for this {cfg.targetLabel.toLowerCase()}
            <span className="text-slate-500">
              {' — '}
              {form.target_role === 'pharmacy_staff' && 'manages catalogue, prices, stock'}
              {form.target_role === 'lab_staff' && 'manages catalogue, prices, TAT'}
              {form.target_role === 'radiology_staff' && 'manages catalogue, prices, TAT'}
              {form.target_role === 'ambulance_staff' && 'manages coverage, tariffs, fleet'}
              {form.target_role === 'org_admin' && 'primary contact — receives billing notices'}
            </span>
          </span>
        </label>

        {/* Ambulance-only descriptive label so the invitee's
            AmbulanceStaffLink carries the right job title from day
            one — no follow-up Django admin edit needed. Other roles
            don't have a role_title counterpart on their StaffLink
            model, so the field is hidden for them. */}
        {form.target_role === 'ambulance_staff' && (
          <label className="block mt-3">
            <span className="text-xs text-slate-600">Role title (optional)</span>
            <input
              value={form.role_title}
              onChange={set('role_title')}
              placeholder="e.g. Paramedic, EMT, Driver, Dispatcher, On-call Clinician, Nurse"
              className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
              maxLength={80}
            />
            <span className="block mt-1 text-[11px] text-slate-500">
              Descriptive only — permissions come from the &quot;Ambulance
              Staff&quot; group. Editable later via Django admin.
            </span>
          </label>
        )}

        <label className="block mt-3">
          <span className="text-xs text-slate-600">Internal note (optional)</span>
          <textarea
            rows={2}
            value={form.note}
            onChange={set('note')}
            placeholder="Visible to admins only"
            className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
          />
        </label>

        {create.isError && <div className="mt-3"><ErrorBanner error={create.error} /></div>}

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => create.mutate()}
            disabled={!canSubmit}
            className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
          >
            {create.isPending ? 'Creating invitation…' : 'Send invitation'}
          </button>
        </div>
      </Card>

      {/* ─── Existing invitations list ───────────────────────────── */}
      <Card>
        <CardHeader
          title="Invitations"
          subtitle="Pending, accepted, expired, and revoked"
        />

        <div className="flex flex-wrap gap-2 mb-3">
          <FilterPills
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All' },
              { value: 'pending', label: 'Pending' },
              { value: 'accepted', label: 'Accepted' },
              { value: 'expired', label: 'Expired' },
              { value: 'revoked', label: 'Revoked' },
            ]}
          />
          <FilterPills
            label="Role"
            value={roleFilter}
            onChange={setRoleFilter}
            options={[
              { value: 'all', label: 'All' },
              ...TARGET_ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            ]}
          />
        </div>

        {invitesQ.error && <ErrorBanner error={invitesQ.error} onRetry={() => invitesQ.refetch()} />}

        <ul className="divide-y divide-slate-100">
          {invitesQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
          {!invitesQ.isLoading && filteredInvites.length === 0 && (
            <li className="py-3 text-sm text-slate-400">
              {invitesQ.data?.length ? 'No invitations match these filters.' : 'No staff invitations yet.'}
            </li>
          )}
          {filteredInvites.map((inv) => (
            <li key={inv.id} className="py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {[inv.first_name, inv.last_name].filter(Boolean).join(' ') || inv.email}
                    <span className="text-slate-500 ml-2 text-xs">&lt;{inv.email}&gt;</span>
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(inv.effective_status)}`}>
                      {inv.effective_status}
                    </span>
                    <span className="font-medium">{inv.role_label || inv.target_role}</span>
                    {inv.target_name && <span>@ {inv.target_name}</span>}
                    {inv.is_admin && (
                      <span className="text-amber-700 font-medium">admin</span>
                    )}
                    <span>Invited {formatDateTime(inv.created_at)}</span>
                    {inv.expires_at && <span>Expires {formatDateTime(inv.expires_at)}</span>}
                    {inv.invited_by_username && <span>by {inv.invited_by_username}</span>}
                    {inv.accepted_user_username && <span>→ user @{inv.accepted_user_username}</span>}
                  </div>
                  {inv.effective_status === 'pending' && inv.invite_url && (
                    <div className="mt-2 text-xs flex items-center gap-2 font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1 max-w-full">
                      <a
                        href={inv.invite_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-wantok-700 underline truncate min-w-0"
                      >
                        {inv.invite_url}
                      </a>
                      <button
                        type="button"
                        onClick={() => copyLink(inv.invite_url)}
                        className="text-slate-500 underline decoration-dotted shrink-0"
                      >
                        Copy
                      </button>
                    </div>
                  )}
                </div>
                {inv.effective_status === 'pending' && (
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => resend.mutate(inv.id)}
                      disabled={resend.isPending}
                      className="text-xs text-wantok-700 underline"
                      title="Send the invite email again with the same token"
                    >
                      {resend.isPending && resend.variables === inv.id ? 'Sending…' : 'Resend email'}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Revoke invitation for ${inv.email}?`)) {
                          revoke.mutate(inv.id)
                        }
                      }}
                      disabled={revoke.isPending}
                      className="text-xs text-rose-700 underline"
                    >
                      Revoke
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------

function LabeledInput({ label, ...rest }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      <input {...rest} className="border rounded px-2 py-1.5 text-sm" />
    </label>
  )
}

function LabeledSelect({ label, children, ...rest }) {
  return (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      <span>{label}</span>
      <select {...rest} className="border rounded px-2 py-1.5 text-sm bg-white disabled:bg-slate-50">
        {children}
      </select>
    </label>
  )
}

function FilterPills({ label, value, onChange, options }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-1">{label}</span>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={
            'text-[11px] rounded-full border px-2 py-0.5 transition ' +
            (value === opt.value
              ? 'bg-wantok-700 border-wantok-700 text-white'
              : 'bg-white border-slate-300 text-slate-700 hover:border-wantok-400')
          }
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}
